import React from 'react';
import KaAlert from '../Common/KaAlert'
import { UserProfile } from '../UserAccess/UserContext';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import { HttpMethod } from '../Common/useFetch';
import { useDisplayMessage } from '../Common/useDisplayMessage';
import EntityModal, { ModalMode } from '../Common/EntityModal';
import CustomerDto from '../Common/dtos/CustomerDto';
import CustomerUpsertDto from '../Common/dtos/CustomerUpsertDto';
import { Path, useFieldArray, useForm } from 'react-hook-form';
import { FormTextControl, RowFormText } from '../Common/Forms/FormText';
import { Col, FormGroup } from 'reactstrap';
import BoldButton from '../Common/BoldButton';
import { FormLabel } from '../Common/Forms/FormLabel';

interface IProps {
  user: UserProfile,
  initialCustomer: CustomerDto | null,
  onHide: () => void,
  onSuccess: (customer: { name: string; accountNumber?: string }) => void,
  mode: ModalMode
}

const customersPath = (businessId: string) => `/api/businesses/${businessId}/customers`;

const postSpec = (businessId: string): UserRequestSpec => ({
  path: customersPath(businessId),
  method: HttpMethod.POST
});

const putSpec = (businessId: string, customerId: string): UserRequestSpec => ({
  path: `${customersPath(businessId)}/${customerId}`,
  method: HttpMethod.PUT
});

type CustomerForm = Omit<CustomerUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

const newCustomer = (): CustomerUpsertDto =>
({
  name: '', accountNumber: '', billingAddressStreet: '',
  billingAddressCity: '', billingAddressState: '', billingAddressPostalCode: '',
  billingAddressCountry: '', emailAddresses: [], ticketNotes: ''
});

export const CreateEditCustomer = (props: IProps) => {
  const create = props.mode === ModalMode.Create;
  let { id, ...initialCustomer } = { ...props.initialCustomer };
  const customer = create ? newCustomer() : initialCustomer;
  const initEmails = initialCustomer.emailAddresses?.map(email => ({ email })) ?? [];

  const businessId = props.user.selectedBusiness?.id ?? "";
  const requestSpec = create
    ? postSpec(businessId)
    : putSpec(businessId, id ?? "");

  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<CustomerForm>({
    defaultValues: {
      ...customer,
      emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
    }
  });

  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

  const validateUniqueEmail = (value: string) => {
    if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    trigger('emailAddresses');
  }

  const handleCreateUpdate = (formValue: CustomerForm) => {
    useCreateEdit.request({
      ...formValue,
      emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
    });
  }

  const useCreateEdit = useUserRequest<CustomerUpsertDto>(requestSpec, {
    onSuccess: () => props.onSuccess({ name: getValues().name!, accountNumber: getValues().accountNumber }),
    onError: (e) => displayMessage.fail(e.message),
  });

  const hookProps = (name: Path<CustomerForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const onClose = () => {
    props.onHide();
    displayMessage.clear();
  }

  const labelSpace = 4;
  const inputSpace = 7;

  const emailControl = (index: number) =>
    <FormTextControl
      {...hookProps(`emailAddresses.${index}.email`)}
      displayName='Email address'
      options={{
        onChange: () => trigger('emailAddresses'),
        validate: validateUniqueEmail,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Email Address is invalid'
        }
      }} />

  return (<EntityModal
    mode={props.mode}
    entityName="customer"
    onHide={onClose}
    isSaving={useCreateEdit.isLoading}
    onSubmit={handleSubmit(handleCreateUpdate)}>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText
        {...hookProps("name")}
        options={{
          required: 'Name is required',
          validate: {
            length: v => v.length < 51 || 'Name cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("accountNumber")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Account Number cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressStreet")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address Street cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressCity")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address City cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressState")}
        options={{
          validate: {
            length: v => v.length < 51 || 'Billing Address State cannot be longer than 50 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressPostalCode")}
        options={{
          validate: {
            length: v => v.length < 16 || 'Billing Address Postal Code cannot be longer than 15 characters'
          }
        }}
      />
      <RowFormText
        {...hookProps("billingAddressCountry")}
        options={{
          validate: {
            length: v => v.length < 101 || 'Billing Address Country cannot be longer than 100 characters'
          }
        }}
      />
      {
        emailAddresses.fields.map((field, index) => {
          const isFirst = index === 0;
          const showX = emailAddresses.fields.length > 1;
          return <FormGroup row key={field.id}>
            {isFirst &&
              <Col xs={{ size: labelSpace }}>
                <FormLabel displayName='Email' {...hookProps(`emailAddresses.${index}.email`)} />
              </Col>
            }
            <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
              {emailControl(index)}
            </Col>
            {
              showX && props.mode !== ModalMode.View &&
              <Col xs="1" className="pe-0">
                <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
              </Col>
            }
          </FormGroup>
        })}
      <FormGroup row>
        <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={emailAddresses.fields.length >= 10}
            onClick={() => emailAddresses.append({ email: '' })}>
            Add Email
          </BoldButton>
        </Col>
      </FormGroup>
      <RowFormText
        {...hookProps("ticketNotes")}
      />
  </EntityModal>);
}
